import { type FC } from 'react';
import { Group, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { type NavbarTab } from '../../types';
import styles from './Tab.module.css';

interface TabProps {
    tab: NavbarTab;
    onClose?: VoidFunction;
    compact?: boolean;
}

export const Tab: FC<TabProps> = ({ tab, onClose, compact }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useBreakpoint('mobile');

    const { icon: Icon, label, to, onClick, isActive } = tab;

    const active = isActive?.(location.pathname);

    const linkClasses = classNames(styles.link, {
        [styles.compact]: compact,
    });

    const handleClick = async () => {
        if (onClick) {
            onClick();
        }

        if (to) {
            await navigate(to);
        }

        onClose?.();
    };

    return (
        <Tooltip
            label={label}
            position='right'
            transitionProps={{
                duration: 0,
            }}
            disabled={isMobile}
        >
            <UnstyledButton
                w='100%'
                p='sm'
                className={linkClasses}
                onClick={handleClick}
                data-active={active || undefined}
                aria-label={label}
            >
                {compact ? (
                    <Icon
                        size={20}
                        stroke={1.5}
                    />
                ) : (
                    <Group gap='xs'>
                        <Icon stroke={1.5} />

                        <Text size='md'>{label}</Text>
                    </Group>
                )}
            </UnstyledButton>
        </Tooltip>
    );
};
